import { Box, Button, ThemeProvider, Typography } from "@mui/material";
import { toast, ToastContainer as ToastifyContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useColors from "../api/hooks/useColors";
import SvgToIcon from "../icons/SvgToIcon";
import {
  logos,
  severities,
} from "../scenes/stack/environment/icons/iconExport";
import { ColorModeContext, useMode } from "../theme";

export default function ToastContainer(props) {
  return (
    <ToastifyContainer
      position={toast.POSITION.TOP_RIGHT}
      autoClose={5000}
      hideProgressBar={true}
      newestOnTop={true}
      rtl={false}
      pauseOnHover={true}
      closeButton={false}
      closeOnClick={false}
      padding={0}
      toastStyle={{
        background: "none",
        boxShadow: "none",
        maxWidth: "550px",
        width: "auto",
        overflow: "visible",
        padding: "0px",
        marginBottom: "0rem",
      }}
      style={{
        maxWidth: "550px",
        width: "auto",
        padding: "0px",
        marginBottom: "0rem",
      }}
      {...props}
    />
  );
}

const ToastIconBox = ({ icon }) => (
  <Box
    sx={{
      display: "flex",
      width: "24px",
      justifyContent: "center",
      alignItems: "flex-start",
      alignSelf: "stretch",
      borderRadius: "8px",
    }}
  >
    {icon}
  </Box>
);

const ToastIcon = ({ type }) => {
  const iconMap = {
    success: severities.SeverityBackgroundSuccessIcon,
    warning: severities.SeverityBackgroundWarningIcon,
    info: severities.SeverityBackgroundInfoIcon,
    error: severities.SeverityErrorIcon,
  };

  return (
    <ToastIconBox
      icon={
        <SvgToIcon
          IconLogo={iconMap[type]}
          width="24px"
          height="24px"
          style={{ flexShrink: 0 }}
        />
      }
    />
  );
};

const ToastClosingIcon = () => {
  const handleClose = () => toast.dismiss();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
      }}
      onClick={handleClose}
    >
      <Box
        sx={{
          display: "flex",
          width: "28px",
          height: "28px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <SvgToIcon
          IconLogo={logos.CloseIcon}
          width="18px"
          height="18px"
          style={{ flexShrink: "0" }}
        />
      </Box>
    </Box>
  );
};

const ToastButton = ({ text, callback = null, text_color, style = {} }) => {
  const baseButtonStyle = {
    display: "flex",
    height: "32px",
    minWidth: "64px",
    padding: "8px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    borderRadius: "10px",
    whiteSpace: "nowrap",
  };
  const mergedStyles = { ...baseButtonStyle, ...style };
  return (
    <Button style={mergedStyles} onClick={callback}>
      <Typography
        variant="overline"
        sx={{
          fontStyle: "normal",
          lineHeight: "18px",
          color: text_color,
        }}
      >
        {text}
      </Typography>
    </Button>
  );
};

const SimpleToast = ({
  icon,
  params,
  border_left,
  text_color,
  background_color,
}) => {
  const colors = useColors();
  return (
    <Box
      sx={{
        display: "inline-flex",
        padding: "12px 8px 12px 12px",
        alignItems: "center",
        gap: "16px",
        borderRadius: "8px",
        borderLeft: `2px solid ${border_left}`,
        background: colors.common_white,
        boxShadow: `0px 8px 16px 0px ${colors.grey_16percent}`,
        maxWidth: "550px",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          padding: "0px 8px",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {icon}
        <Typography
          variant="subtitle2"
          sx={{
            alignSelf: "stretch",
            color: colors.text_primary,
            fontStyle: "normal",
            lineHeight: "22px",
            wordBreak: "break-word",
            hyphens: "auto",
          }}
        >
          {params.text}
        </Typography>
      </Box>
      {params.recommended_action && (
        <Box
          sx={{
            display: "flex",
            padding: "8px 4px 8px 8px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <ToastButton
            text={params.recommended_action}
            callback={params.recommended_action_callback}
            text_color={text_color}
            style={{
              padding: "0px 8px",
              borderRadius: "8px",
              height: "30px",
              background: background_color,
            }}
          />
          {params.unrecommended_action && (
            <ToastButton
              text={params.unrecommended_action}
              callback={params.unrecommended_action_callback}
              text_color={colors.text_secondary}
              style={{
                padding: "0px 8px",
                borderRadius: "8px",
                height: "32px",
                background: colors.white_0percent,
              }}
            />
          )}
        </Box>
      )}
      <ToastClosingIcon />
    </Box>
  );
};

const ComplexToast = ({
  icon,
  params,
  border_left,
  background_color,
  text_color,
}) => {
  const colors = useColors();
  return (
    <Box
      sx={{
        display: "inline-flex",
        padding: "16px",
        alignItems: "flex-start",
        gap: "16px",
        borderRadius: "8px",
        borderLeft: `2px solid ${border_left}`,
        background: colors.common_white,
        boxShadow: `0px 4px 16px 0px ${colors.grey_16percent}`,
        maxWidth: "550px",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          paddingRight: "16px",
          alignItems: "flex-start",
          gap: "12px",
          alignSelf: "stretch",
        }}
      >
        {icon}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "12px",
            marginRight: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                alignSelf: "stretch",
                color: colors.text_primary,
                fontStyle: "normal",
                lineHeight: "22px",
                wordBreak: "break-word",
                hyphens: "auto",
              }}
            >
              {params.text}
            </Typography>
            {(params.content || params.comment) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "4px",
                }}
              >
                {params.content && (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: colors.text_secondary,
                      fontStyle: "normal",
                      lineHeight: "22px",
                    }}
                  >
                    {params.content}
                  </Typography>
                )}
                {params.comment && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: "normal",
                      color: colors.text_secondary,
                      lineHeight: "18px",
                    }}
                  >
                    {params.comment}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {(params.recommended_action || params.unrecommended_action) && (
            <Box
              sx={{
                display: "flex",
                padding: "8px 16px 8px 0px",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {params.recommended_action && (
                <ToastButton
                  text={params.recommended_action}
                  callback={params.recommended_action_callback}
                  text_color={text_color}
                  style={{ background: background_color }}
                />
              )}
              {params.unrecommended_action && (
                <ToastButton
                  text={params.unrecommended_action}
                  callback={params.unrecommended_action_callback}
                  text_color={colors.text_secondary}
                  style={{ background: colors.white_0percent }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: "12px",
          right: "12px",
        }}
      >
        <ToastClosingIcon />
      </Box>
    </Box>
  );
};

// Complex toast is used when there is a comment or content
// Simple toast is used when there is only a text without comment or content
const ToastWrapper = ({ icon, params, type }) => {
  const [theme, colorMode] = useMode();
  const isSimpleToast = !params.comment && !params.content;
  const colors = useColors();
  const border_colors_map = {
    success: colors.success_300_main,
    info: colors.primary_300main,
    error: colors.error_main,
    warning: colors.warning_dark,
  };
  const buttons_background_map = {
    success: colors.success_soft_label_fill,
    info: colors.primary_8percent,
    error: colors.error_8percent,
    warning: colors.warning_yellow_8percent,
  };

  const text_color_map = {
    success: colors.success_dark,
    info: colors.primary_tab,
    error: colors.error_main,
    warning: colors.warning_dark,
  };
  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <Box>
          {isSimpleToast ? (
            <SimpleToast
              icon={icon}
              params={params}
              border_left={border_colors_map[type]}
              text_color={text_color_map[type]}
              background_color={buttons_background_map[type]}
            />
          ) : (
            <ComplexToast
              icon={icon}
              params={params}
              border_left={border_colors_map[type]}
              background_color={buttons_background_map[type]}
              text_color={text_color_map[type]}
            />
          )}
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

const checkType = (args) => {
  const isString = typeof args === "string";
  const {
    text,
    content = null,
    comment = null,
    recommended_action = null,
    unrecommended_action = null,
    recommended_action_callback = null,
    unrecommended_action_callback = null,
  } = isString ? { text: args } : args;
  return {
    text,
    content,
    comment,
    recommended_action,
    unrecommended_action,
    recommended_action_callback,
    unrecommended_action_callback,
  };
};

export const Toast = {
  success: (args) => {
    const params = checkType(args);
    toast.success(
      <ToastWrapper
        icon={<ToastIcon type="success" />}
        params={params}
        type={"success"}
      />,
      {
        icon: false,
      }
    );
  },
  info: (args) => {
    const params = checkType(args);
    toast.info(
      <ToastWrapper
        icon={<ToastIcon type="info" />}
        params={params}
        type={"info"}
      />,
      {
        icon: false,
      }
    );
  },
  error: (args) => {
    const params = checkType(args);
    toast.error(
      <ToastWrapper
        icon={<ToastIcon type="error" />}
        params={params}
        type={"error"}
      />,
      {
        icon: false,
      }
    );
  },
  warning: (args) => {
    const params = checkType(args);
    toast.warning(
      <ToastWrapper
        icon={<ToastIcon type="warning" />}
        params={params}
        type={"warning"}
      />,

      {
        icon: false,
      }
    );
  },
};
